/**
 * The full map (M): a surveyor's chart you can zoom and pan. Scroll or pinch to zoom, drag to
 * move, double-click or double-tap to zoom in. Regions are labelled (faded until you've found them), your quest step has a pin; banks,
 * shops, workplaces, waystones, people and landmarks you've seen get little icons (with their
 * names when you're zoomed in), and the legend switches each kind on and off. Tap an icon to see
 * what it is and walk there; tap charted ground to walk there.
 */
import { NPCS, RESOURCES, SHOPS, bandDef, isBandKind, type BandKind, type GameMap, type MapObject, type ResourceKind } from '@sunderfen/shared';
import type { MapState, Maps } from './maps.js';

export type PoiKind = 'bank' | 'shop' | 'work' | 'travel' | 'people' | 'landmark' | 'danger' | 'resource';
interface Poi { kind: PoiKind; glyph: Glyph; x: number; z: number; name: string; detail: string; obj: MapObject }
type Glyph = 'bank' | 'deposit' | 'shop' | 'anvil' | 'furnace' | 'flame' | 'bench' | 'waystone' | 'ferry' | 'person' | 'landmark' | 'camp' | 'block' | 'fish' | 'rock' | 'tree' | 'cache';

export const POI_KINDS: { kind: PoiKind; label: string; color: string }[] = [
  { kind: 'bank', label: 'Banks', color: '#c99a2e' },
  { kind: 'shop', label: 'Shops & stalls', color: '#3f8a4a' },
  { kind: 'work', label: 'Workplaces', color: '#c0602a' },
  { kind: 'travel', label: 'Travel', color: '#2a8aa0' },
  { kind: 'people', label: 'People', color: '#7a4a9a' },
  { kind: 'landmark', label: 'Landmarks', color: '#a8862a' },
  { kind: 'danger', label: 'Danger', color: '#9c2f22' },
  { kind: 'resource', label: 'Resources', color: '#4a7a3a' },
];
const COLOR = Object.fromEntries(POI_KINDS.map(k => [k.kind, k.color])) as Record<PoiKind, string>;
const FILTER_KEY = 'hollowmere-map-filters';
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

function poisOf(map: GameMap): Poi[] {
  const out: Poi[] = [];
  const add = (kind: PoiKind, glyph: Glyph, o: MapObject, name: string, detail = '') => out.push({ kind, glyph, x: o.x + (o.w ?? 1) / 2, z: o.z + (o.h ?? 1) / 2, name, detail, obj: o });
  for (const o of map.objects) {
    const nm = o.props?.name ? String(o.props.name) : '';
    if (o.type === 'station') {
      switch (o.kind) {
        case 'chest': add('bank', 'bank', o, nm || 'Bank', 'Store and take out your things.'); break;
        case 'deposit': add('bank', 'deposit', o, nm || 'Deposit box', 'Put things in your bank. Nothing comes out here.'); break;
        case 'merchant': add('shop', 'shop', o, nm || 'Trader', 'Buys and sells.'); break;
        case 'anvil': add('work', 'anvil', o, nm || 'Anvil', 'Smithing: hammer bars into tools and gear.'); break;
        case 'furnace': add('work', 'furnace', o, nm || 'Furnace', 'Smelt ore into bars.'); break;
        case 'range': add('work', 'flame', o, nm || 'Cooking range', 'Cook here: it burns less than a fire.'); break;
        case 'campfire': add('work', 'flame', o, nm || 'Campfire', 'Cook here.'); break;
        case 'workbench': add('work', 'bench', o, nm || 'Fletching bench', 'Fletch bows and arrows.'); break;
        case 'mill': add('work', 'bench', o, nm || 'Quern', 'Grind wheat into flour.'); break;
        case 'tanning_rack': add('work', 'bench', o, nm || 'Tanning rack', 'Tan hides into leather (Crafting).'); break;
        case 'spinning_wheel': add('work', 'bench', o, nm || 'Spinning wheel', 'Spin wool into yarn (Crafting).'); break;
        case 'compost_bin': add('work', 'bench', o, nm || 'Compost bin', 'Rot weeds into compost.'); break;
        case 'waystone': add('travel', 'waystone', o, nm || 'Waystone', 'Touch it once, then travel between waystones.'); break;
        case 'ferry': add('travel', 'ferry', o, nm || 'Ferry', 'Crossings by boat.'); break;
        case 'cache': case 'stash': add('danger', 'cache', o, nm || 'Stash', 'Something hidden here.'); break;
      }
    } else if (o.type === 'prop' && o.kind === 'stall') {
      const keeper = String(o.props?.keeper ?? ''), shop = SHOPS[keeper];
      add('shop', 'shop', o, shop?.name ?? `${cap(String(o.props?.style ?? 'market'))} stall`, shop?.blurb ?? '');
    } else if (o.type === 'npc' && o.kind !== 'villager') {
      const d = (NPCS as Record<string, { name: string; title: string } | undefined>)[o.kind];
      if (d && !out.some(p => p.kind === 'shop' && p.name === SHOPS[o.kind]?.name)) add('people', 'person', o, d.name, d.title);
    } else if (o.type === 'landmark') add('landmark', 'landmark', o, nm || cap(o.kind), 'A landmark: find it for Exploration xp.');
    else if (o.type === 'camp' && isBandKind(o.props?.band) && bandDef(o.props.band as BandKind).mode === 'camp') add('danger', 'camp', o, String(o.props?.name ?? 'Raiders\' camp'), 'Raiders. Go in strong.');
    else if (o.type === 'patch') add('resource', 'tree', o, ({ herb: 'Herb patch', tree: 'Tree patch', fruit: 'Fruit tree patch' } as Record<string, string>)[o.kind] ?? 'Allotment', 'Farming: your own crop grows here.');
    else if (o.type === 'obstacle') add('danger', 'block', o, nm || cap(o.kind.replace(/_/g, ' ')), 'Something in the way: clear it to pass.');
    else if (o.type === 'resource' && !o.props?.hidden) {
      const r = RESOURCES[o.kind as ResourceKind];
      if (r) add('resource', r.skill === 'fishing' ? 'fish' : r.skill === 'mining' ? 'rock' : 'tree', o, cap(o.kind.replace(/_/g, ' ')), cap(r.skill));
    }
  }
  return out;
}

/** A small badge with a glyph, drawn at (x, y) screen pixels. */
function badge(x: CanvasRenderingContext2D, g: Glyph, cx: number, cy: number, r: number, color: string, dim = false) {
  x.save(); x.translate(cx, cy);
  x.globalAlpha = dim ? 0.55 : 1;
  x.beginPath(); x.arc(0, 0, r, 0, Math.PI * 2); x.fillStyle = color; x.fill();
  x.lineWidth = Math.max(1.2, r * 0.18); x.strokeStyle = '#f6ecd2'; x.stroke();
  x.fillStyle = '#fff8e8'; x.strokeStyle = '#fff8e8'; x.lineWidth = Math.max(1.1, r * 0.14); x.lineJoin = 'round'; x.lineCap = 'round';
  const s = r * 0.55;
  x.beginPath();
  switch (g) {
    case 'bank': x.rect(-s * 0.85, -s * 0.6, s * 1.7, s * 1.3); x.stroke(); x.beginPath(); x.arc(0, 0.05 * s, s * 0.3, 0, Math.PI * 2); x.fill(); break;
    case 'deposit': x.rect(-s * 0.8, -s * 0.5, s * 1.6, s * 1.2); x.stroke(); x.beginPath(); x.moveTo(-s * 0.5, -s * 0.1); x.lineTo(s * 0.5, -s * 0.1); x.stroke(); break;
    case 'shop': x.moveTo(-s * 0.75, -s * 0.2); x.lineTo(s * 0.75, -s * 0.2); x.lineTo(s * 0.6, s * 0.8); x.lineTo(-s * 0.6, s * 0.8); x.closePath(); x.fill(); x.beginPath(); x.arc(0, -s * 0.2, s * 0.42, Math.PI, 0); x.stroke(); break;
    case 'anvil': x.moveTo(-s, -s * 0.35); x.lineTo(s * 0.9, -s * 0.35); x.lineTo(s * 0.4, s * 0.15); x.lineTo(s * 0.3, s * 0.55); x.lineTo(s * 0.7, s * 0.85); x.lineTo(-s * 0.7, s * 0.85); x.lineTo(-s * 0.3, s * 0.55); x.lineTo(-s * 0.4, s * 0.15); x.closePath(); x.fill(); break;
    case 'furnace': x.rect(-s * 0.75, -s * 0.5, s * 1.5, s * 1.35); x.fill(); x.fillStyle = color; x.beginPath(); x.arc(0, s * 0.35, s * 0.38, Math.PI, 0); x.fill(); x.fillStyle = '#fff8e8'; x.fillRect(s * 0.25, -s, s * 0.35, s * 0.6); break;
    case 'flame': x.moveTo(0, -s); x.quadraticCurveTo(s * 0.9, -s * 0.1, s * 0.5, s * 0.75); x.quadraticCurveTo(0, s * 1.05, -s * 0.5, s * 0.75); x.quadraticCurveTo(-s * 0.9, 0, 0, -s); x.fill(); break;
    case 'bench': x.rect(-s, -s * 0.2, s * 2, s * 0.35); x.fill(); x.fillRect(-s * 0.8, 0, s * 0.25, s * 0.8); x.fillRect(s * 0.55, 0, s * 0.25, s * 0.8); break;
    case 'waystone': x.moveTo(0, -s); x.lineTo(s * 0.7, s * 0.85); x.lineTo(-s * 0.7, s * 0.85); x.closePath(); x.fill(); break;
    case 'ferry': x.moveTo(-s, s * 0.1); x.lineTo(s, s * 0.1); x.lineTo(s * 0.6, s * 0.7); x.lineTo(-s * 0.6, s * 0.7); x.closePath(); x.fill(); x.fillRect(-s * 0.08, -s, s * 0.16, s); x.beginPath(); x.moveTo(s * 0.08, -s); x.lineTo(s * 0.7, -s * 0.15); x.lineTo(s * 0.08, -s * 0.15); x.fill(); break;
    case 'person': x.arc(0, -s * 0.4, s * 0.4, 0, Math.PI * 2); x.fill(); x.beginPath(); x.arc(0, s * 0.9, s * 0.75, Math.PI, 0); x.fill(); break;
    case 'landmark': for (let i = 0; i < 10; i++) { const a = -Math.PI / 2 + (i * Math.PI) / 5, rr = i % 2 ? s * 0.45 : s; x.lineTo(Math.cos(a) * rr, Math.sin(a) * rr); } x.closePath(); x.fill(); break;
    case 'camp': x.moveTo(0, -s); x.lineTo(s * 0.95, s * 0.8); x.lineTo(-s * 0.95, s * 0.8); x.closePath(); x.fill(); x.fillStyle = color; x.beginPath(); x.moveTo(0, -s * 0.1); x.lineTo(s * 0.3, s * 0.8); x.lineTo(-s * 0.3, s * 0.8); x.fill(); break;
    case 'block': x.moveTo(-s * 0.65, -s * 0.65); x.lineTo(s * 0.65, s * 0.65); x.moveTo(s * 0.65, -s * 0.65); x.lineTo(-s * 0.65, s * 0.65); x.stroke(); break;
    case 'cache': x.rect(-s * 0.7, -s * 0.35, s * 1.4, s * 1.05); x.fill(); x.fillStyle = color; x.fillRect(-s * 0.15, -s * 0.05, s * 0.3, s * 0.3); break;
    case 'fish': x.ellipse(-s * 0.15, 0, s * 0.65, s * 0.38, 0, 0, Math.PI * 2); x.fill(); x.beginPath(); x.moveTo(s * 0.4, 0); x.lineTo(s * 0.95, -s * 0.45); x.lineTo(s * 0.95, s * 0.45); x.fill(); break;
    case 'rock': x.moveTo(-s * 0.8, s * 0.6); x.lineTo(-s * 0.5, -s * 0.4); x.lineTo(0, -s * 0.8); x.lineTo(s * 0.7, -s * 0.2); x.lineTo(s * 0.8, s * 0.6); x.closePath(); x.fill(); break;
    case 'tree': x.moveTo(0, -s); x.lineTo(s * 0.75, s * 0.4); x.lineTo(-s * 0.75, s * 0.4); x.closePath(); x.fill(); x.fillRect(-s * 0.15, s * 0.35, s * 0.3, s * 0.55); break;
  }
  x.restore();
}

export class WorldMap {
  private pois: Poi[];
  private cx = 256; private cz = 256; private zoom = 2;
  private filters: Record<PoiKind, boolean>;
  private ptrs = new Map<number, { x: number; y: number }>();
  private drag: { x: number; y: number; cx: number; cz: number; moved: boolean; pinch?: { d: number; zoom: number; mx: number; my: number; cx: number; cz: number } } | null = null;
  private lastTap = 0;
  private raf = 0;
  private state: MapState | null = null;
  private hover: Poi | null = null;
  private picked: Poi | null = null;
  open = false;

  constructor(private map: GameMap, private maps: Maps, private cv: HTMLCanvasElement, private tip: HTMLElement, private onWalk: (x: number, z: number, o?: MapObject) => void, private onNote: (s: string) => void) {
    this.pois = poisOf(map);
    let saved: Partial<Record<PoiKind, boolean>> = {};
    try { saved = JSON.parse(localStorage.getItem(FILTER_KEY) ?? '{}'); } catch { /* none */ }
    this.filters = Object.fromEntries(POI_KINDS.map(k => [k.kind, saved[k.kind] ?? k.kind !== 'resource'])) as Record<PoiKind, boolean>;
    cv.addEventListener('pointerdown', e => this.down(e));
    cv.addEventListener('pointermove', e => this.move(e));
    cv.addEventListener('pointerup', e => this.up(e));
    cv.addEventListener('pointercancel', e => { this.ptrs.delete(e.pointerId); this.drag = null; });
    cv.addEventListener('pointerleave', () => { if (!this.ptrs.size) { this.hover = null; if (!this.picked) this.tip.hidden = true; this.redraw(); } });
    cv.addEventListener('wheel', e => { e.preventDefault(); const r = cv.getBoundingClientRect(); this.zoomAt(e.clientX - r.left, e.clientY - r.top, Math.pow(1.0018, -e.deltaY)); }, { passive: false });
    cv.addEventListener('dblclick', e => { const r = cv.getBoundingClientRect(); this.zoomAt(e.clientX - r.left, e.clientY - r.top, 2); });
    cv.style.touchAction = 'none';
    new ResizeObserver(() => { if (this.open) this.redraw(); }).observe(cv);
  }

  get filterState() { return this.filters; }
  setFilter(k: PoiKind, on: boolean) { this.filters[k] = on; try { localStorage.setItem(FILTER_KEY, JSON.stringify(this.filters)); } catch { /* ignore */ } this.redraw(); }

  /** Show it centred on the player, close enough to read the town around you. */
  show(s: MapState) {
    const first = !this.open;
    this.open = true; this.state = s;
    if (first) { this.cx = s.px; this.cz = s.pz; this.zoom = Math.max(this.minZoom(), 5); this.picked = null; }
    this.redraw();
  }
  update(s: MapState) { this.state = s; if (this.open) this.redraw(); }
  hide() { this.open = false; this.tip.hidden = true; }
  centre() { if (this.state) { this.cx = this.state.px; this.cz = this.state.pz; this.redraw(); } }
  zoomBy(f: number) { const r = this.cv.getBoundingClientRect(); this.zoomAt(r.width / 2, r.height / 2, f); }
  fit() { this.zoom = this.minZoom(); this.cx = this.map.width / 2; this.cz = this.map.height / 2; this.redraw(); }

  private size() { const r = this.cv.getBoundingClientRect(); return { w: Math.max(1, r.width), h: Math.max(1, r.height) }; }
  private minZoom() { const { w, h } = this.size(); return Math.min(w / this.map.width, h / this.map.height) * 0.98; }
  private clampView() {
    const { w, h } = this.size(), mz = this.minZoom();
    this.zoom = Math.min(16, Math.max(mz, this.zoom));
    const hw = w / 2 / this.zoom, hh = h / 2 / this.zoom;
    this.cx = this.map.width / 2 <= hw ? this.map.width / 2 : Math.min(this.map.width - hw, Math.max(hw, this.cx));
    this.cz = this.map.height / 2 <= hh ? this.map.height / 2 : Math.min(this.map.height - hh, Math.max(hh, this.cz));
  }
  private toTile(sx: number, sy: number) { const { w, h } = this.size(); return [this.cx + (sx - w / 2) / this.zoom, this.cz + (sy - h / 2) / this.zoom] as const; }
  private toScreen(tx: number, tz: number) { const { w, h } = this.size(); return [(tx - this.cx) * this.zoom + w / 2, (tz - this.cz) * this.zoom + h / 2] as const; }
  private zoomAt(sx: number, sy: number, f: number) {
    const [tx, tz] = this.toTile(sx, sy);
    this.zoom *= f; this.clampView();
    const [nx, nz] = this.toTile(sx, sy);
    this.cx += tx - nx; this.cz += tz - nz;
    this.redraw();
  }

  /* ---------------- input ---------------- */
  private down(e: PointerEvent) {
    this.cv.setPointerCapture(e.pointerId);
    this.ptrs.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (this.ptrs.size === 1) this.drag = { x: e.clientX, y: e.clientY, cx: this.cx, cz: this.cz, moved: false };
    else if (this.ptrs.size === 2 && this.drag) {
      const [a, b] = [...this.ptrs.values()], r = this.cv.getBoundingClientRect();
      this.drag.moved = true;
      this.drag.pinch = { d: Math.hypot(a.x - b.x, a.y - b.y), zoom: this.zoom, mx: (a.x + b.x) / 2 - r.left, my: (a.y + b.y) / 2 - r.top, cx: this.cx, cz: this.cz };
    }
  }
  private move(e: PointerEvent) {
    const r = this.cv.getBoundingClientRect();
    if (!this.ptrs.has(e.pointerId)) { this.hoverAt(e.clientX - r.left, e.clientY - r.top, e.clientX, e.clientY); return; }
    this.ptrs.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const d = this.drag; if (!d) return;
    if (d.pinch && this.ptrs.size >= 2) {
      const [a, b] = [...this.ptrs.values()], p = d.pinch;
      this.zoom = p.zoom; this.cx = p.cx; this.cz = p.cz;
      const [tx, tz] = this.toTile(p.mx, p.my);
      this.zoom = p.zoom * Math.hypot(a.x - b.x, a.y - b.y) / Math.max(1, p.d); this.clampView();
      const mx = (a.x + b.x) / 2 - r.left, my = (a.y + b.y) / 2 - r.top, [nx, nz] = this.toTile(mx, my);
      this.cx += tx - nx; this.cz += tz - nz; this.clampView(); this.redraw();
      return;
    }
    if (!d.moved && Math.hypot(e.clientX - d.x, e.clientY - d.y) < 6) return;
    d.moved = true;
    this.cx = d.cx - (e.clientX - d.x) / this.zoom; this.cz = d.cz - (e.clientY - d.y) / this.zoom;
    this.clampView(); this.redraw();
  }
  private up(e: PointerEvent) {
    this.ptrs.delete(e.pointerId);
    const d = this.drag;
    if (this.ptrs.size) { if (d) { d.pinch = undefined; const [p] = [...this.ptrs.values()]; d.x = p.x; d.y = p.y; d.cx = this.cx; d.cz = this.cz; } return; }
    this.drag = null;
    if (!d || d.moved) return;
    const r = this.cv.getBoundingClientRect(), sx = e.clientX - r.left, sy = e.clientY - r.top, now = performance.now();
    // a quick second tap zooms in (touch has no dblclick)
    if (e.pointerType !== 'mouse' && now - this.lastTap < 300) { this.lastTap = 0; this.zoomAt(sx, sy, 2); return; }
    this.lastTap = now;
    const hit = this.poiAt(sx, sy);
    if (hit) { this.picked = hit; this.showTip(hit, e.clientX, e.clientY, true); this.redraw(); return; }
    this.picked = null; this.tip.hidden = true;
    const [tx, tz] = this.toTile(sx, sy), x = Math.floor(tx), z = Math.floor(tz);
    if (!this.map.inBounds(x, z)) return;
    if (!this.maps.revealed[this.map.idx(x, z)]) { this.onNote('That ground is uncharted. Walk closer to map it first.'); return; }
    this.onWalk(x, z);
  }
  private hoverAt(sx: number, sy: number, cx: number, cy: number) {
    if (this.picked) return;
    const hit = this.poiAt(sx, sy);
    if (hit !== this.hover) { this.hover = hit; this.redraw(); }
    if (hit) this.showTip(hit, cx, cy, false); else this.tip.hidden = true;
  }
  private showTip(p: Poi, cx: number, cy: number, withGo: boolean) {
    const t = this.tip; t.hidden = false; t.replaceChildren();
    const b = document.createElement('b'); b.textContent = p.name; t.appendChild(b);
    if (p.detail) { const s = document.createElement('span'); s.textContent = p.detail; t.appendChild(s); }
    if (withGo) {
      const go = document.createElement('button'); go.type = 'button'; go.className = 'btn small'; go.textContent = 'Walk there';
      go.onclick = () => { this.tip.hidden = true; this.onWalk(Math.floor(p.x), Math.floor(p.z), p.obj); };
      t.appendChild(go);
    }
    const host = t.offsetParent?.getBoundingClientRect() ?? { left: 0, top: 0, width: innerWidth, height: innerHeight };
    t.style.left = `${Math.min(host.width - t.offsetWidth - 8, Math.max(8, cx - host.left + 14))}px`;
    t.style.top = `${Math.min(host.height - t.offsetHeight - 8, Math.max(8, cy - host.top - t.offsetHeight - 10))}px`;
  }
  private visible(p: Poi) {
    const s = this.state; if (!s) return false;
    if (!this.filters[p.kind]) return false;
    if (p.kind === 'resource' && !s.surveyor) return false;
    if (p.kind === 'resource' && s.depleted.has(p.obj.id)) return false;
    if (p.obj.type === 'obstacle' && s.cleared.has(p.obj.id)) return false;
    const t = this.map.idx(Math.min(Math.floor(p.x), this.map.width - 1), Math.min(Math.floor(p.z), this.map.height - 1));
    return !!this.maps.revealed[t];
  }
  private radius(p: Poi) { const big = p.kind === 'bank' || p.kind === 'shop' || p.kind === 'travel'; return p.kind === 'resource' ? Math.min(7, 2 + this.zoom * 0.5) : Math.min(13, Math.max(big ? 7 : 6, this.zoom * (big ? 1.6 : 1.3))); }
  private poiAt(sx: number, sy: number) {
    let best: Poi | null = null, bd = Infinity;
    for (const p of this.pois) {
      if (!this.visible(p) || (p.kind === 'resource' && this.zoom < 4)) continue;
      const [x, y] = this.toScreen(p.x, p.z), d = Math.hypot(x - sx, y - sy), r = this.radius(p) + 6;
      if (d < r && d < bd) { bd = d; best = p; }
    }
    return best;
  }

  /* ---------------- drawing ---------------- */
  redraw() { if (!this.raf) this.raf = requestAnimationFrame(() => { this.raf = 0; this.draw(); }); }
  private draw() {
    const s = this.state; if (!s || !this.open) return;
    this.clampView();
    const { w, h } = this.size(), dpr = Math.min(2, window.devicePixelRatio || 1), cv = this.cv;
    if (cv.width !== Math.round(w * dpr) || cv.height !== Math.round(h * dpr)) { cv.width = Math.round(w * dpr); cv.height = Math.round(h * dpr); }
    const x = cv.getContext('2d')!;
    x.setTransform(1, 0, 0, 1, 0, 0); x.fillStyle = '#e8d9b2'; x.fillRect(0, 0, cv.width, cv.height);
    // the land, in tile space
    x.setTransform(dpr * this.zoom, 0, 0, dpr * this.zoom, dpr * (w / 2 - this.cx * this.zoom), dpr * (h / 2 - this.cz * this.zoom));
    x.imageSmoothingEnabled = true; x.imageSmoothingQuality = 'high';
    x.filter = 'sepia(0.3) saturate(0.85)';
    this.maps.drawLayers(x);
    x.filter = 'none';
    for (const t of s.traps) { x.fillStyle = '#d83a2a'; x.beginPath(); x.arc(t.x + 0.5, t.z + 0.5, Math.max(0.5, 4 / this.zoom), 0, Math.PI * 2); x.fill(); }
    // charted treasure, danger rings
    for (const o of this.map.objects) {
      if (o.type === 'resource' && o.props?.hidden && s.charts.includes(o.id)) {
        const r = Math.max(1.6, 8 / this.zoom); x.fillStyle = '#c99a2e'; x.strokeStyle = '#4b3a22'; x.lineWidth = r * 0.15;
        x.beginPath(); for (let i = 0; i < 10; i++) { const a = -Math.PI / 2 + (i * Math.PI) / 5, rr = i % 2 ? r * 0.45 : r; x.lineTo(o.x + 0.5 + Math.cos(a) * rr, o.z + 0.5 + Math.sin(a) * rr); } x.closePath(); x.fill(); x.stroke();
      }
    }
    if (this.filters.danger) for (const p of this.pois) if (p.glyph === 'camp' && this.visible(p)) {
      const r = Number(p.obj.props?.radius ?? 6) + 3; x.strokeStyle = '#9c2f22'; x.lineWidth = Math.max(0.5, 2 / this.zoom); x.setLineDash([1.6, 1.1]);
      x.beginPath(); x.arc(p.x, p.z, r, 0, Math.PI * 2); x.stroke(); x.setLineDash([]); x.fillStyle = 'rgba(156,47,34,.12)'; x.fill();
    }
    if (s.goal) { x.strokeStyle = '#b8860b'; x.lineWidth = Math.max(0.6, 2.5 / this.zoom); x.beginPath(); x.arc(s.goal[0] + 0.5, s.goal[1] + 0.5, Math.max(3, 14 / this.zoom), 0, Math.PI * 2); x.stroke(); x.fillStyle = 'rgba(241,191,85,.4)'; x.fill(); }
    // everything else in screen pixels, so icons and text stay the same size at any zoom
    x.setTransform(dpr, 0, 0, dpr, 0, 0); x.imageSmoothingEnabled = true;
    // region names: bigger when zoomed out, fading as you zoom right in
    x.textAlign = 'center'; x.textBaseline = 'middle';
    const rf = Math.round(Math.min(26, Math.max(13, 11 + this.zoom * 1.4)));
    x.font = `700 ${rf}px "Alegreya SC", Georgia, serif`;
    x.globalAlpha = this.zoom > 14 ? 0.45 : 1;
    const baseAlpha = x.globalAlpha;
    for (const r of this.map.regions) {
      if (!r.label) continue;
      const found = s.regions.includes(r.key);
      const [sx, sy] = this.toScreen(r.label[0], r.label[1]);
      if (sx < -100 || sy < -30 || sx > w + 100 || sy > h + 30) continue;
      // places you haven't found yet are named in faded ink, so you know where to head
      x.globalAlpha = found ? baseAlpha : baseAlpha * 0.42;
      x.lineWidth = rf / 3.5; x.strokeStyle = 'rgba(232,217,178,.9)'; x.strokeText(r.name, sx, sy);
      x.fillStyle = found ? '#3a2c18' : '#6a5c48'; x.fillText(r.name, sx, sy);
    }
    // the named places inside a region (a farm, a harbour, a ruin), once you're close enough to read them
    if (this.zoom >= 3) {
      const pf = Math.round(Math.min(17, 9 + this.zoom * 0.8));
      x.font = `italic 600 ${pf}px "Alegreya SC", Georgia, serif`;
      x.globalAlpha = Math.min(1, (this.zoom - 3) / 2) * baseAlpha;
      for (const pl of this.map.places) {
        const [sx, sy0] = this.toScreen(pl.x + 0.5, pl.z + 0.5);
        // step out of the way of a region name sitting on top of it
        const clash = this.map.regions.find(r => { if (!r.label) return false; const [rx, ry] = this.toScreen(r.label[0], r.label[1]); return Math.abs(rx - sx) < 90 && Math.abs(ry - sy0) < rf; });
        const sy = clash ? this.toScreen(clash.label![0], clash.label![1])[1] + rf * 0.6 + pf * 0.7 : sy0;
        if (sx < -80 || sy < -20 || sx > w + 80 || sy > h + 20) continue;
        x.lineWidth = pf / 3.5; x.strokeStyle = 'rgba(232,217,178,.85)'; x.strokeText(pl.name, sx, sy);
        x.fillStyle = '#4a3a24'; x.fillText(pl.name, sx, sy);
      }
    }
    x.globalAlpha = 1;
    // where your quest wants you: a pin you can't miss, at any zoom
    if (s.goal) {
      const [gx, gy] = this.toScreen(s.goal[0] + 0.5, s.goal[1] + 0.5);
      if (gx > -20 && gy > -40 && gx < w + 20 && gy < h + 20) {
        x.save(); x.translate(gx, gy);
        x.beginPath(); x.moveTo(0, 0); x.bezierCurveTo(-9, -12, -11, -20, 0, -26); x.bezierCurveTo(11, -20, 9, -12, 0, 0);
        x.fillStyle = '#d9a42a'; x.fill(); x.lineWidth = 1.6; x.strokeStyle = '#5a3a10'; x.stroke();
        x.beginPath(); x.arc(0, -17, 3.6, 0, Math.PI * 2); x.fillStyle = '#fff6dc'; x.fill();
        x.font = '700 11px system-ui, sans-serif'; x.textAlign = 'center'; x.lineWidth = 3; x.strokeStyle = 'rgba(232,217,178,.95)'; x.strokeText('Quest', 0, -33); x.fillStyle = '#5a3a10'; x.fillText('Quest', 0, -33);
        x.restore();
      }
    }
    // icons, and their names when there's room
    const boxes: [number, number, number, number][] = [];
    const showNames = this.zoom >= 6;
    x.font = `600 12px system-ui, sans-serif`;
    const order: PoiKind[] = ['resource', 'danger', 'landmark', 'people', 'work', 'travel', 'shop', 'bank'];
    for (const kind of order) for (const p of this.pois) {
      if (p.kind !== kind || !this.visible(p)) continue;
      if (p.kind === 'resource' && this.zoom < 4) continue;
      const [sx, sy] = this.toScreen(p.x, p.z);
      if (sx < -20 || sy < -20 || sx > w + 20 || sy > h + 20) continue;
      const r = this.radius(p), hot = p === this.hover || p === this.picked;
      const dim = p.glyph === 'waystone' && !s.waystones?.includes(p.obj.id) || p.glyph === 'landmark' && !s.landmarks.includes(p.obj.id);
      badge(x, p.glyph, sx, sy, hot ? r * 1.25 : r, COLOR[p.kind], dim);
      if ((showNames && p.kind !== 'resource' && p.kind !== 'danger') || hot) {
        const tw = x.measureText(p.name).width, bx = sx - tw / 2 - 3, by = sy + r + 3;
        if (!hot && boxes.some(([a, b, c, d]) => bx < a + c && bx + tw + 6 > a && by < b + d && by + 15 > b)) continue;
        boxes.push([bx, by, tw + 6, 15]);
        x.fillStyle = 'rgba(246,236,210,.92)'; x.beginPath(); x.roundRect(bx, by, tw + 6, 15, 4); x.fill();
        x.fillStyle = '#3a2c18'; x.fillText(p.name, sx, by + 8);
      }
    }
    // you
    const [px, py] = this.toScreen(s.px + 0.5, s.pz + 0.5);
    x.save(); x.translate(px, py); x.rotate(Math.atan2(Math.cos(s.face), Math.sin(s.face)));
    x.fillStyle = '#2b2014'; x.strokeStyle = '#f6ecd2'; x.lineWidth = 2.5;
    x.beginPath(); x.moveTo(11, 0); x.lineTo(-8, -7); x.lineTo(-4, 0); x.lineTo(-8, 7); x.closePath(); x.stroke(); x.fill(); x.restore();
    // north, and the scale
    x.fillStyle = '#4b3a22'; x.font = '700 16px "Alegreya SC", Georgia, serif'; x.fillText('N', w - 22, 20);
    x.beginPath(); x.moveTo(w - 22, 30); x.lineTo(w - 28, 44); x.lineTo(w - 16, 44); x.fill();
    const tiles = [5, 10, 25, 50, 100, 200].find(t => t * this.zoom > 60) ?? 200, bar = tiles * this.zoom;
    x.fillRect(14, h - 18, bar, 3); x.font = '600 11px system-ui, sans-serif'; x.textAlign = 'left'; x.fillText(`${tiles} paces`, 14, h - 28);
  }
}
